import { useCallback } from "react";
import {
  getErrorMessages,
  useGetAccountsSummaryQuery,
} from "../../../../services";
import type { ErrorResponse } from "../../../../services/types";
import {
  UseAccountsDataReturn,
  UseAccountsDataParams,
  AccountTotals,
} from "./types";

// refetchAccounts is kept only for the error-retry path; mutations already invalidate AccountSummary.
export const useAccountsData = ({
  yearSelected,
  activeOnly,
}: UseAccountsDataParams): UseAccountsDataReturn => {
  const summaryQuery = useGetAccountsSummaryQuery({
    year: yearSelected.value,
    activeOnly: activeOnly.value,
  });
  const anyAccountsQuery = useGetAccountsSummaryQuery({ activeOnly: false });

  const summary = summaryQuery.data;
  const accounts = summary?.accounts ?? [];

  let totals: AccountTotals | null = null;
  if (summary) {
    totals = {
      currentAmount: summary.currentAmount,
      inAmount: summary.inAmount,
      outAmount: summary.outAmount,
    };
  }

  const hasAnyAccounts = (anyAccountsQuery.data?.accounts.length ?? 0) > 0;
  const isCheckingAccountExistence =
    accounts.length === 0 &&
    !summaryQuery.isLoading &&
    (anyAccountsQuery.isLoading || anyAccountsQuery.isFetching);

  let error: string | null = null;
  if (summaryQuery.isError) {
    const messages = getErrorMessages(summaryQuery.error as ErrorResponse);
    error = messages[0] || "Failed to load account data";
  }

  const refetchAccounts = useCallback(async () => {
    await summaryQuery.refetch();
  }, [summaryQuery]);

  return {
    accounts,
    totals,
    hasAnyAccounts,
    isLoading: summaryQuery.isLoading || isCheckingAccountExistence,
    error,
    refetchAccounts,
  };
};
